import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = {
  key: 0,
  class: "form__indicator"
}
const _hoisted_3 = { class: "form__tooltip" }
const _hoisted_4 = ["id"]

import { requiredTextEditor, Rule } from '@/validation/rules'
import { Status, validate } from '@/validation'
import { ref, watch } from "vue";
import { QuillEditor } from "@vueup/vue-quill";
import BlotFormatter from "quill-blot-formatter";
import { ImageDrop } from "quill-image-drop-module";
import MagicUrl from 'quill-magic-url'
import MarkdownShortcuts from 'quill-markdown-shortcuts';
import FormTextEditorLinkPopup from "@/components/forms/FormTextEditorLinkPopup.vue";

//The doc tell us to load styles like this:
// import "@vueup/vue-quill/dist/vue-quill.snow.css";
//but they dont show up in prod. So I stocked them in our project directly :
import "@/assets/plugins/vue-quill/vue-quill.snow.min.css";
import { useI18n } from "vue3-i18n";

// eslint-disable-next-line

export default /*#__PURE__*/_defineComponent({
  __name: 'FormTextEditor',
  props: {
    name: {},
    label: {},
    modelValue: {},
    tooltip: {},
    rules: {}
  },
  emits: ["update:modelValue", "validated"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

// eslint-disable-next-line
__expose({
  //to call validation in parent.
  validateInput : validateTextEditor
})

const { t } = useI18n()

const toolbarOptions = [
  ["clean"], // remove formatting button

  ["bold", "italic"], // toggled buttons

  [{ list: "ordered" }, { list: "bullet" }],
  [{ indent: "-1" }, { indent: "+1" }], // outdent/indent

  [{ header: [2, 3, 4, false] }],

  [{ color: ["#009DE2", "#E0F5FF", "#417CBB", "#D3D3D3", "#5C5C5C", "#000000", "#FFFFFF", "#E68B53", "#EE8A69", "#538C41", "#77B544", "#45AFC3", "#8EC64C", "#1B7A3E", "#FFDEAF", "#66C7C8", "#E72926"] }], // dropdown with defaults from theme
  [{ align: [] }],

  [ 'link', 'image' ],
];

const modules = [
  {
    name: 'blotFormatter', //so images can be resized in editor
    module: BlotFormatter,
    options: {}
  },
  {
    name: "imageDrop",
    module: ImageDrop,
    options: {}
  },
  {
    name: 'magicUrl', //so urls are recognize when pasted.
    module: MagicUrl,
    options: {}
  },
  {
    name: 'markdownShortcuts',
    module: MarkdownShortcuts,
    options: {}
  }
]

// eslint-disable-next-line
const emit = __emit;

const contentModel = ref(props.modelValue);
watch(props, (newProps) => contentModel.value = newProps.modelValue);
const status = ref<Status>({ valid: true });
const isRequired = !(props.rules != null && props.rules.length == 0);

const showQuillEditorLinkPopup = ref<boolean>(false);
const quillEditor = ref<typeof QuillEditor | null>(null);
watch(quillEditor, (editor) => {
  //since Quill cant do it itself after 8 (!!!) years, I'm customizing the add link input.
  const quill = editor?.getQuill();
  const toolbar = quill.getModule("toolbar");

  if (toolbar) {
    const showLinkPopup = () => {
      showQuillEditorLinkPopup.value = true;
    }

    toolbar.addHandler("link", showLinkPopup)
  }
});

function handleChange() {
  const value = quillEditor.value?.getContents();
  validateTextEditor();

  contentModel.value = value
  emit("update:modelValue", value);
}

function handleBlur() {
  validateTextEditor();
}

function validateTextEditor() {
  const value = quillEditor.value?.getContents();
  let validationRules = props.rules ? props.rules : [requiredTextEditor]
  status.value = validate(value, validationRules)

  emit("validated", props.name, status.value);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["form__field", {'error':!status.value.valid}])
  }, [
    (showQuillEditorLinkPopup.value)
      ? (_openBlock(), _createBlock(FormTextEditorLinkPopup, {
          key: 0,
          onClosePopup: _cache[0] || (_cache[0] = () => showQuillEditorLinkPopup.value = false),
          "quill-editor": quillEditor.value,
          name: _ctx.name
        }, null, 8, ["quill-editor", "name"]))
      : _createCommentVNode("", true),
    _createVNode(_unref(QuillEditor), {
      ref_key: "quillEditor",
      ref: quillEditor,
      toolbar: toolbarOptions,
      modules: modules,
      "content-type": "html",
      content: contentModel.value,
      "onUpdate:content": [
        _cache[1] || (_cache[1] = ($event: any) => ((contentModel).value = $event)),
        handleChange
      ],
      id: _ctx.name,
      onBlur: handleBlur,
      "aria-invalid": !status.value.valid,
      "aria-describedby": `error__${_ctx.name}`
    }, null, 8, ["content", "id", "aria-invalid", "aria-describedby"]),
    _createElementVNode("label", { for: _ctx.name }, [
      _createTextVNode(_toDisplayString(_ctx.label ? _ctx.label : _ctx.name) + " ", 1),
      isRequired
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
        : _createCommentVNode("", true),
      _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.tooltip ? `${_ctx.tooltip}<br/>` : "") + " " + _toDisplayString(_unref(t)("textEditorLinkPopup.tooltip")), 1)
    ], 8, _hoisted_1),
    (!status.value.valid)
      ? (_openBlock(), _createElementBlock("span", {
          key: 1,
          class: "form__error-message",
          id: `error__${_ctx.name}`
        }, _toDisplayString(status.value.message), 9, _hoisted_4))
      : _createCommentVNode("", true)
  ], 2))
}
}

})